import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Card } from '@diwata/ironvale'
import { SharingAPI, type AcceptResult, type InvitePreview } from '@/api/sharing'
import { ApiError } from '@/api/client'
import { useUI } from '@/store/ui'

/** What each role can do in a shared region — shown so the visitor knows what
 *  they're agreeing to before they accept. */
const ROLE_NOTE: Record<string, string> = {
  viewer: 'view the domain, read-only',
  contributor: 'propose changes for the owner to approve',
  editor: 'edit the domain directly',
  owner: 'own this domain',
}

/** /invite/:token — the landing side of an invite link. Auth-gated by the layout,
 *  so a stranger logs in (or registers) first and lands back here; the guard parks
 *  the token as `pendingInvite` and Login routes back so the token is never lost.
 *
 *  We show a PREVIEW (who shared, which region, what role) and let the visitor
 *  Accept or Decline — no silent auto-join on mount. */
export function Invite() {
  const { token } = useParams()
  const { diwaUrl, setPendingInvite } = useUI()
  const navigate = useNavigate()
  const api = useMemo(() => new SharingAPI(diwaUrl), [diwaUrl])
  const [preview, setPreview] = useState<InvitePreview | null>(null)
  const [result, setResult] = useState<AcceptResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [accepting, setAccepting] = useState(false)

  // We're here holding the token, so the pending-intent hand-off is complete —
  // clear it, or a later logout/login would bounce back to this stale link.
  useEffect(() => {
    setPendingInvite(null)
  }, [setPendingInvite])

  useEffect(() => {
    if (!token) return
    let live = true
    api.preview(token)
      .then((p) => live && setPreview(p))
      .catch((err) => {
        if (!live) return
        if (err instanceof ApiError && err.status === 410) {
          setError('This invite link is no longer open — ask for a fresh one.')
        } else {
          setError("Couldn't open the invite — try again in a moment.")
        }
      })
    return () => {
      live = false
    }
  }, [api, token])

  function accept() {
    if (!token) return
    setAccepting(true)
    setError(null)
    api.accept(token).then(setResult).catch((err) => {
      if (err instanceof ApiError && err.status === 410) {
        setError('This invite link is no longer open — ask for a fresh one.')
      } else if (err instanceof ApiError && err.status === 400) {
        setError("That's your own domain — you already hold it.")
      } else {
        setError("Couldn't accept the invite — try again in a moment.")
      }
    }).finally(() => setAccepting(false))
  }

  const roleNote = preview ? (ROLE_NOTE[preview.role] ?? preview.role) : ''

  return (
    <div style={{ maxWidth: 480, margin: '80px auto' }}>
      <Card>
        <div className="dw-rail-section-title">Joining a shared domain</div>

        {!preview && !result && !error && <p className="dw-empty">Opening the invite…</p>}
        {error && <p className="dw-error">{error}</p>}

        {/* The accepted, joined state. */}
        {result && (
          <div style={{ fontSize: 'calc(13px * var(--ae-text-scale, 1))' }}>
            <p style={{ marginBottom: 10 }}>
              {result.already_member ? 'You already hold ' : 'You now hold '}
              <strong>{result.name}</strong> as a <strong>{result.role}</strong> —
              the domain appears among your domains, marked as shared.
            </p>
            <Link className="dw-link" to="/domains">open Domains →</Link>
          </div>
        )}

        {/* The preview + choice, before any commitment. */}
        {preview && !result && !error && (
          <div style={{ fontSize: 'calc(13px * var(--ae-text-scale, 1))' }}>
            {preview.is_own ? (
              <>
                <p style={{ marginBottom: 10 }}>
                  <strong>{preview.name}</strong> is your own domain — you already hold it.
                </p>
                <Link className="dw-link" to="/domains">open Domains →</Link>
              </>
            ) : (
              <>
                <p style={{ marginBottom: 6 }}>
                  {preview.invited_by ? <><strong>{preview.invited_by}</strong> invited you</> : 'You’ve been invited'}
                  {' '}to join <strong>{preview.name}</strong>.
                </p>
                <p className="dw-empty" style={{ marginBottom: 14 }}>
                  You’ll join as a <strong>{preview.role}</strong> — you can {roleNote}.
                  {preview.already_member && ' You already hold this domain; accepting refreshes your role.'}
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="dw-btn" onClick={accept} disabled={accepting}>
                    {accepting ? 'Joining…' : preview.already_member ? 'Confirm' : 'Accept'}
                  </button>
                  <button className="dw-action-btn" onClick={() => navigate('/home')} disabled={accepting}>
                    Decline
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}
